/**
 * Main JavaScript for RadianHub Theme
 * 
 * @package RadianHub
 * @since 1.0.0
 */

// Import Tailwind CSS + globals.css
import '../css/globals.css';

// Import modules
import { initNavigation } from './modules/navigation';
import { initForms } from './modules/forms';
import { initCaseStudyFilter } from './modules/case-study-filter';
import { initAcceleratorFilter } from './modules/accelerator-filter';

/**
 * Initialize all modules when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
    // Initialize all modules
    initNavigation();
    initForms();
    initCaseStudyFilter();
    initAcceleratorFilter();
    
    // Initialize any other modules
    // RadianHub Theme initialized successfully
});

/**
 * Handle window load events
 */
window.addEventListener('load', () => {
    // Remove loading class
    document.body.classList.remove('loading');
    
    // Initialize any load-dependent modules
    // RadianHub Theme fully loaded
});

/**
 * Handle window resize events
 */
let resizeTimer;
window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
        // Handle resize events
        // Window resized
    }, 250);
});

/**
 * Handle scroll events
 */
let scrollTimer;
window.addEventListener('scroll', () => {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
        // Handle scroll events
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        document.body.classList.toggle('scrolled', scrollTop > 100);
    }, 10);
});

/**
 * Utility functions
 */
window.RadianHub = {
    // Smooth scroll to element
    scrollTo: (element, offset = 0) => {
        const target = typeof element === 'string' ? document.querySelector(element) : element;
        if (target) {
            const targetPosition = target.offsetTop - offset;
            window.scrollTo({
                top: targetPosition,
                behavior: 'smooth'
            });
        }
    },
    
    // Toggle class on element
    toggleClass: (element, className) => {
        if (element) {
            element.classList.toggle(className);
        }
    },
    
    // Add class to element
    addClass: (element, className) => {
        if (element) {
            element.classList.add(className);
        }
    },
    
    // Remove class from element
    removeClass: (element, className) => {
        if (element) {
            element.classList.remove(className);
        }
    },
    
    // Check if element is in viewport
    isInViewport: (element) => {
        const rect = element.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    },
    
    // Debounce function
    debounce: (func, wait) => {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    },
    
    // Throttle function
    throttle: (func, limit) => {
        let inThrottle;
        return function() {
            const args = arguments;
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    }
};
